// hooks/usePullToRefresh.ts — Touch-drag physics for a native-style
// pull-to-refresh gesture, bound to a scrollable container.
//
// Only intervenes when the drag starts at the container's scroll top (0) and
// moves downward — every other touch interaction (normal scrolling, upward
// drags, horizontal gestures) passes through untouched.

import { useEffect, useRef, useState } from 'react';

export type PullToRefreshStatus = 'idle' | 'pulling' | 'ready' | 'refreshing';

interface UsePullToRefreshOptions {
  /** Called when the user releases past the threshold. Awaited before resetting. */
  onRefresh: () => Promise<void> | void;
  /** Disables the gesture entirely (e.g. routes with their own pan/scroll handling). */
  disabled?: boolean;
  /** Drag distance (px) required to trigger a refresh on release. */
  threshold?: number;
  /** Visual cap (px) on how far the indicator/content can be dragged. */
  maxPull?: number;
}

interface UsePullToRefreshResult {
  containerRef: React.RefObject<HTMLDivElement | null>;
  status: PullToRefreshStatus;
  pullDistance: number;
  /** Programmatic trigger — the single-pointer alternative to the drag gesture. */
  refresh: () => Promise<void>;
}

const DEFAULT_THRESHOLD = 64;
const DEFAULT_MAX_PULL = 96;
const RESISTANCE = 0.45;

export function usePullToRefresh({
  onRefresh,
  disabled = false,
  threshold = DEFAULT_THRESHOLD,
  maxPull = DEFAULT_MAX_PULL,
}: UsePullToRefreshOptions): UsePullToRefreshResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<PullToRefreshStatus>('idle');
  const [pullDistance, setPullDistance] = useState(0);

  // Refs so the gesture listeners (bound once) always see the latest values
  // without needing to be torn down and rebound on every render.
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const statusRef = useRef(status);
  statusRef.current = status;
  const startYRef = useRef<number | null>(null);
  const isPullingRef = useRef(false);

  const runRefresh = async () => {
    if (statusRef.current === 'refreshing') return;
    setStatus('refreshing');
    setPullDistance(threshold * 0.8);
    try {
      await onRefreshRef.current();
    } finally {
      setStatus('idle');
      setPullDistance(0);
    }
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || disabled) return;

    const onTouchStart = (e: TouchEvent) => {
      if (statusRef.current === 'refreshing' || el.scrollTop > 0) {
        isPullingRef.current = false;
        startYRef.current = null;
        return;
      }
      startYRef.current = e.touches[0]!.clientY;
      isPullingRef.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isPullingRef.current || startYRef.current === null) return;
      const delta = e.touches[0]!.clientY - startYRef.current;
      if (delta <= 0 || el.scrollTop > 0) {
        isPullingRef.current = false;
        setPullDistance(0);
        setStatus('idle');
        return;
      }
      // Prevent the native scroll/bounce only once we're actively dragging
      // the indicator open — everything else keeps default touch behavior.
      e.preventDefault();
      const resisted = Math.min(maxPull, delta * RESISTANCE);
      setPullDistance(resisted);
      setStatus(resisted >= threshold ? 'ready' : 'pulling');
    };

    const onTouchEnd = () => {
      if (!isPullingRef.current) return;
      isPullingRef.current = false;
      startYRef.current = null;
      if (statusRef.current === 'ready') {
        void runRefresh();
      } else {
        setStatus('idle');
        setPullDistance(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, threshold, maxPull]);

  return {
    containerRef,
    status,
    pullDistance,
    refresh: runRefresh,
  };
}
